import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { projectStatuses, approvalStatuses } from '../../constants/statusOptions';
import { updateProject } from '../../services/projectService';
import type { Project } from '../../types/project';

interface EditProjectDialogProps {
  open: boolean;
  project: Project | null;
  onClose: () => void;
  onUpdated: (project: Project) => void;
}

export const EditProjectDialog = ({
  open,
  project,
  onClose,
  onUpdated,
}: EditProjectDialogProps) => {
  const [projectName, setProjectName] = useState('');
  const [projectContact, setProjectContact] = useState('');
  const [status, setStatus] = useState('Active');
  const [approvalStatus, setApprovalStatus] = useState('Pending');
  const [expectedStartDate, setExpectedStartDate] = useState('');
  const [expectedEndDate, setExpectedEndDate] = useState('');
  const [comments, setComments] = useState('');
  const [reportUnit, setReportUnit] = useState<'metric' | 'imperial'>('metric');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (project && open) {
      setProjectName(project.ProjectName || '');
      setProjectContact(project.ProjectContact || '');
      setStatus(project.Status || 'Active');
      setApprovalStatus(project.ApprovalStatus || 'Pending');
      setExpectedStartDate(formatDateForInput(project.ExpectedStartDate));
      setExpectedEndDate(formatDateForInput(project.ExpectedEndDate));
      setComments(project.Comments || '');
      setReportUnit(((project as any).ReportUnit as 'metric' | 'imperial') || 'metric');
      setError('');
    }
  }, [project, open]);

  const handleSubmit = async () => {
    if (!project) return;
    setSubmitting(true);
    setError('');
    try {
      const updated = await updateProject(project.ProjectKey, {
        ProjectName: projectName.trim(),
        Status: status,
        ApprovalStatus: approvalStatus,
        ExpectedStartDate: expectedStartDate || undefined,
        ExpectedEndDate: expectedEndDate || undefined,
        ProjectContact: projectContact.trim() || undefined,
        ReportUnit: reportUnit,
        Comments: comments.trim() || undefined,
      } as any);
      onUpdated(updated);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || 'Failed to update project.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!project) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Edit Project — {project.ProjectId}</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <TextField size="small" label="Project Name" value={projectName}
          onChange={(e) => setProjectName(e.target.value)} />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField size="small" select label="Status" value={status}
            onChange={(e) => setStatus(e.target.value)}>
            {projectStatuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Approval Status" value={approvalStatus}
            onChange={(e) => setApprovalStatus(e.target.value)}>
            {approvalStatuses.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField size="small" type="date" label="Expected Start Date" value={expectedStartDate}
            onChange={(e) => setExpectedStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label="Expected End Date" value={expectedEndDate}
            onChange={(e) => setExpectedEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField size="small" label="Project Contact" value={projectContact}
            onChange={(e) => setProjectContact(e.target.value)} />
          <TextField size="small" select label="Report Units" value={reportUnit}
            onChange={(e) => setReportUnit(e.target.value as 'metric' | 'imperial')}
            helperText="Depth display in PDF reports">
            <MenuItem value="metric">Metric (m)</MenuItem>
            <MenuItem value="imperial">Imperial (ft)</MenuItem>
          </TextField>
        </Box>

        <TextField size="small" label="Comments" multiline rows={2} value={comments}
          onChange={(e) => setComments(e.target.value)} />

        {error && <Typography variant="body2" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function formatDateForInput(value: string | null | undefined): string {
  if (!value) return '';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
  } catch {
    return '';
  }
}
